import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Loading from '../../components/common/Loading/Loading';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import DataTable from '../../components/common/DataTable/DataTable';
import Pagination from '../../components/common/Pagination/Pagination';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import FormSelect from '../../components/common/FormSelect/FormSelect';
import Icon from '../../components/common/Icon/Icon';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import PlanNameWithProject from '../../components/common/PlanNameWithProject/PlanNameWithProject';
import ProjectBadge from '../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../components/common/RedFlagBadge/RedFlagBadge';
import ClassificationBadge from '../../components/common/ClassificationBadge/ClassificationBadge';
import StatusIndicator from '../../components/common/StatusIndicator';
import Tooltip from '../../components/common/Tooltip';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import SearchableSelect, { SelectOption } from '../../components/common/SearchableSelect/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLeads, LeadFilters } from '../../hooks/useLeads';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import { LeadService, SubscriptionPlanService } from '../../services';
import { useCommercialLeadStatusPolling } from '../../hooks/useCommercialLeadStatusPolling';
import { usePageSize } from '../../hooks/usePageSize';
import { useQueryFilters } from '../../hooks/useQueryFilters';
import type {
  CheckoutLead,
  CheckoutLeadPlan,
  CheckoutSelectedPaymentMethod,
  ExistingStudentContactMatch,
} from '../../types';
import type { ProjectIdentity } from '../../types/project';
import {
  shouldDisplayStudentClassificationBadge,
  getStudentClassificationBadgeColors,
} from '../../types';
import { formatCurrency, formatDateTime } from '../../utils';
import { maskPhoneByCountry } from '../../utils/masks';
import {
  CommercialStage,
  COMMERCIAL_STAGE_OPTIONS,
  canAdvanceCommercialStage,
  canRescueDeclinedLead,
  getCommercialStageLabel,
  normalizeCommercialStage,
} from '../../constants/commercialStage';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import SalesGoalCounter from '../Dashboard/components/SalesGoalCounter';
import { useCommercialSalesGoal } from '../../hooks/useCommercialSalesGoal';
import { getBrazilCurrentCompetenceMonth } from '../../utils/competence';
import CommercialCheckoutLinkModal from './CommercialCheckoutLinkModal';
import CommercialFilters from './CommercialFilters';
import {
  shouldOfferCommercialCheckout,
  formatCommercialOwnerLabel,
  shouldShowCommercialLeadProfileTags,
} from './commercialLead.utils';
import { COMMERCIAL_QUERY_FILTER_SCHEMA } from './commercialQueryFilters';
import {
  COMMERCIAL_DECLINED_GRID_TEMPLATE,
  COMMERCIAL_LEADS_GRID_TEMPLATE,
  getCommercialListingMode,
  showsCommercialPaymentAndCheckout,
} from './commercialNavigation';
import { parseCommercialPaidStatusesFilter } from '../../constants/commercialFilters';
import { canAccessCommercialDashboard, canAssignOtherCommercialOwners } from '../../utils/permissions';
import studentListStyles from '../Students/StudentList/StudentList.module.css';
import styles from './Commercial.module.css';
import { parsePlanBenefits } from '../Checkout/checkout.utils';
import {
  LEAD_QUALIFICATION_BUSY_ROUTINE_OPTIONS,
  LEAD_QUALIFICATION_CONSULTING_EXPECTATION_OPTIONS,
  LEAD_QUALIFICATION_INJURY_LIMITATION_OPTIONS,
  LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS,
  LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS,
  LEAD_QUALIFICATION_START_TIME_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_EXPERIENCE_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_FORMAT_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_OPTIONS,
  LEAD_QUALIFICATION_WEEKLY_COMMITMENT_OPTIONS,
} from '../Checkout/checkout.constants';
import {
  getLeadCancellationReasonLabel,
  isLeadCancellationReason,
  LEAD_CANCELLATION_REASON_OPTIONS,
} from '../../constants/leadCancellationReason';


const BILLING_CYCLE_LABELS: Record<string, string> = {
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semestral: 'Semestral',
  yearly: 'Anual',
};

const PAYMENT_METHOD_LABELS: Record<CheckoutSelectedPaymentMethod, string> = {
  pix: 'Pix',
  boleto: 'Boleto',
  credit_card: 'Cartão de crédito',
};

const STAGE_CHANGE_LOCK_MS = 2_000;

type OwnerEditModalState = {
  qualificationId: number;
  studentName: string;
  ownerId: string;
};

type PendingStageChange = {
  lead: CheckoutLead;
  nextStage: string;
};

interface CommercialProps {
  lockedProjectCode?: string;
}

function getLeadStageSelectOptions(currentStage: string | null | undefined): SelectOption[] {
  const current = normalizeCommercialStage(currentStage);
  return COMMERCIAL_STAGE_OPTIONS.map(({ value, label }) => ({
    value,
    label,
    disabled: Boolean(current) && value !== current && !canAdvanceCommercialStage(current, value),
  }));
}

function getPaymentMethodLabel(method: CheckoutLead['selected_payment_method']): string {
  if (!method) return '—';
  return PAYMENT_METHOD_LABELS[method] ?? method;
}

function getBillingCycleLabel(value: string | null): string {
  if (!value) return '—';
  return BILLING_CYCLE_LABELS[value] ?? value;
}

function getOptionLabel(options: Array<{ value: string; label: string }>, value: string | null): string {
  if (!value) return '—';
  return options.find((option) => option.value === value)?.label ?? value;
}

function formatCommercialLeadPhone(lead: CheckoutLead): string {
  if (!lead.student_phone) return '—';

  const callingCode = lead.student_phone_country_code || '55';
  return `+${callingCode} ${maskPhoneByCountry(lead.student_phone, callingCode)}`;
}

function formatExistingMatchPhone(match: ExistingStudentContactMatch): string | null {
  if (!match.phoneNumber) return null;
  const callingCode = match.phoneCountryCode || '55';
  return `+${callingCode} ${maskPhoneByCountry(match.phoneNumber, callingCode)}`;
}

function resolveLeadPlanProject(
  plan: CheckoutLeadPlan,
  projectByPlanId: Map<number, ProjectIdentity>,
): CheckoutLeadPlan {
  if (plan.project || !plan.id) {
    return plan;
  }

  return {
    ...plan,
    project: projectByPlanId.get(plan.id) ?? null,
  };
}

function openCommercialLeadWhatsApp(lead: CheckoutLead): void {
  if (!lead.student_phone) return;

  const callingCode = (lead.student_phone_country_code || '55').replace(/\D/g, '');
  const digits = lead.student_phone.replace(/\D/g, '');

  if (!digits) return;

  window.open(`https://wa.me/${callingCode}${digits}`, '_blank', 'noopener');
}

const PlanModalContent: React.FC<{ plan: CheckoutLeadPlan }> = ({ plan }) => {
  const benefits = parsePlanBenefits(plan.benefits);

  return (
    <div className={styles['plan-modal-content']}>
      <div className={styles['plan-detail-row']}>
        <span>Nome</span>
        <strong>
          <PlanNameWithProject name={plan.name} project={plan.project} />
        </strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Descrição</span>
        <strong>{plan.description ?? '—'}</strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Valor</span>
        <strong>{typeof plan.price_cents === 'number' ? formatCurrency(plan.price_cents / 100) : '—'}</strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Ciclo</span>
        <strong>{getBillingCycleLabel(plan.billing_cycle)}</strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Fidelidade</span>
        <strong>{typeof plan.loyalty_months === 'number' ? `${plan.loyalty_months} meses` : '—'}</strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Renovação automática</span>
        <strong>{plan.auto_renew ? 'Sim' : 'Não'}</strong>
      </div>
      <div className={styles['plan-detail-row']}>
        <span>Pagamento único</span>
        <strong>{plan.is_single_payment ? 'Sim' : 'Não'}</strong>
      </div>
      {benefits.length > 0 && (
        <div className={styles['plan-benefits']}>
          <span>Benefícios</span>
          <ul>
            {benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const QualificationModalContent: React.FC<{ lead: CheckoutLead }> = ({ lead }) => (
  <div className={styles['plan-modal-content']}>
    <div className={styles['plan-detail-row']}>
      <span>Investimento</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS, lead.qualification?.investment_interest ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Momento</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_START_TIME_OPTIONS, lead.qualification?.start_time_expectation ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Objetivo</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS, lead.qualification?.main_goal ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Relação com o treino</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_OPTIONS, lead.qualification?.training_relationship ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Experiência de treino</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_TRAINING_EXPERIENCE_OPTIONS, lead.qualification?.training_experience ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Rotina apertada</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_BUSY_ROUTINE_OPTIONS, lead.qualification?.busy_routine_behavior ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Dias por semana</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_WEEKLY_COMMITMENT_OPTIONS, lead.qualification?.weekly_commitment_days ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Formato de treino</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_TRAINING_FORMAT_OPTIONS, lead.qualification?.training_format ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Expectativa da consultoria</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_CONSULTING_EXPECTATION_OPTIONS, lead.qualification?.consulting_expectation ?? null)}</strong>
    </div>
    <div className={styles['plan-detail-row']}>
      <span>Lesão ou limitação</span>
      <strong>{getOptionLabel(LEAD_QUALIFICATION_INJURY_LIMITATION_OPTIONS, lead.qualification?.injury_limitation ?? null)}</strong>
    </div>
    {lead.cancellation_reason && (
      <div className={styles['plan-detail-row']}>
        <span>Desistência</span>
        <strong>{getLeadCancellationReasonLabel(lead.cancellation_reason)}</strong>
      </div>
    )}
  </div>
);

const CommercialMonthGoal: React.FC = () => {
  const { roles } = useAuth();
  const canView = canAccessCommercialDashboard(roles);
  const month = useMemo(() => getBrazilCurrentCompetenceMonth(), []);
  const goal = useCommercialSalesGoal(month, canView);

  if (!canView) {
    return null;
  }

  return (
    <div className={styles['goal-summary']}>
      <SalesGoalCounter snapshot={goal.snapshot} loading={goal.loading} error={goal.error} />
    </div>
  );
};

const Commercial: React.FC<CommercialProps> = ({ lockedProjectCode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const listingMode = getCommercialListingMode(location.pathname);
  const isDeclinedListing = listingMode === 'declined';
  const showPaymentAndCheckout = showsCommercialPaymentAndCheckout(listingMode);
  const {
    filters: queryFilters,
    setFilter,
    setFilters,
    resetFilters,
  } = useQueryFilters(COMMERCIAL_QUERY_FILTER_SCHEMA, { pageKey: 'page' });
  const {
    searchName,
    classifications: classificationFilters,
    leadTemperatureTypes: leadTemperatureFilters,
    personaTypes: personaFilters,
    cancellationReasons: cancellationReasonFilters,
    investmentInterests: investmentInterestFilters,
    startTimeExpectations: startTimeFilters,
    mainGoals: mainGoalFilters,
    commercialStages: commercialStageFilters,
    paidStatuses: paidStatusFilters,
    paymentMethods: paymentMethodFilters,
    joinedAtStart,
    joinedAtEnd,
    projectCodes: projectCodeFilters,
    commercialOwnerIds: commercialOwnerIdFilters,
  } = queryFilters;
  const { roles } = useAuth();
  const { setBreadcrumbs } = useBreadcrumbs();
  const projects = useMarketingProjects();
  const lockedProject = lockedProjectCode
    ? projects.find((project) => project.code === lockedProjectCode)
    : undefined;
  const canPickOtherOwners = canAssignOtherCommercialOwners(roles);
  const [selectedPlan, setSelectedPlan] = useState<CheckoutLeadPlan | null>(null);
  const [planProjectById, setPlanProjectById] = useState<Map<number, ProjectIdentity>>(new Map());

  useEffect(() => {
    let cancelled = false;

    SubscriptionPlanService.listProjectByPlanId()
      .then((projectByPlanId) => {
        if (!cancelled) {
          setPlanProjectById(projectByPlanId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlanProjectById(new Map());
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);
  const [selectedQualificationLead, setSelectedQualificationLead] = useState<CheckoutLead | null>(null);
  const [pendingCancellationSave, setPendingCancellationSave] = useState<{
    studentId: number | null;
    qualificationId: number | null;
    studentName: string;
    reason: string;
  } | null>(null);
  const [submittingCancellationReason, setSubmittingCancellationReason] = useState(false);
  const [checkoutModalLead, setCheckoutModalLead] = useState<CheckoutLead | null>(null);
  const [ensuringCheckoutStudent, setEnsuringCheckoutStudent] = useState(false);
  const [existingStudentMatchModal, setExistingStudentMatchModal] = useState<ExistingStudentContactMatch | null>(null);
  const [declinedMissingReasonLead, setDeclinedMissingReasonLead] = useState<CheckoutLead | null>(null);
  const [clintSyncErrorModal, setClintSyncErrorModal] = useState<{
    studentName: string;
    error: string;
  } | null>(null);
  const [commercialOwnerOptions, setCommercialOwnerOptions] = useState<SelectOption[]>([]);
  const [currentPortalUserId, setCurrentPortalUserId] = useState<number | null>(null);
  const [loadingOwnerOptions, setLoadingOwnerOptions] = useState(false);
  const [pendingOwnerEdit, setPendingOwnerEdit] = useState<OwnerEditModalState | null>(null);
  const [submittingOwnerEdit, setSubmittingOwnerEdit] = useState(false);
  const [pendingAssignToSelf, setPendingAssignToSelf] = useState<CheckoutLead | null>(null);
  const [pendingUnassignFromSelf, setPendingUnassignFromSelf] = useState<CheckoutLead | null>(null);
  const [assigningOwnerQualificationId, setAssigningOwnerQualificationId] = useState<number | null>(null);
  const [pendingStageChange, setPendingStageChange] = useState<PendingStageChange | null>(null);
  const [savingStageKey, setSavingStageKey] = useState<string | null>(null);
  const [pendingRescueLead, setPendingRescueLead] = useState<CheckoutLead | null>(null);
  const [savingRescueKey, setSavingRescueKey] = useState<string | null>(null);
  const [stageLockUntilByKey, setStageLockUntilByKey] = useState<Record<string, number>>({});
  const stageLockTimeoutsRef = useRef<Record<string, number>>({});
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('commercial_page_size');

  useEffect(() => {
    if (!lockedProjectCode) {
      return undefined;
    }

    setBreadcrumbs([
      { label: 'Projetos' },
      { label: lockedProject?.name || lockedProjectCode },
    ]);

    return () => setBreadcrumbs([]);
  }, [lockedProject?.name, lockedProjectCode, setBreadcrumbs]);

  const resolvedProjectCodes = useMemo(
    () => (lockedProjectCode ? [lockedProjectCode] : projectCodeFilters),
    [lockedProjectCode, projectCodeFilters],
  );

  const filters: LeadFilters = useMemo(
    () => ({
      searchName: searchName || undefined,
      classifications: classificationFilters.length > 0 ? classificationFilters : undefined,
      leadTemperatureTypes: leadTemperatureFilters.length > 0 ? leadTemperatureFilters : undefined,
      personaTypes: personaFilters.length > 0 ? personaFilters : undefined,
      cancellationReasons: cancellationReasonFilters.length > 0 ? cancellationReasonFilters : undefined,
      investmentInterests: investmentInterestFilters.length > 0 ? investmentInterestFilters : undefined,
      startTimeExpectations: startTimeFilters.length > 0 ? startTimeFilters : undefined,
      mainGoals: mainGoalFilters.length > 0 ? mainGoalFilters : undefined,
      commercialStages: isDeclinedListing || commercialStageFilters.length === 0
        ? undefined
        : commercialStageFilters,
      hasPaid: showPaymentAndCheckout
        ? parseCommercialPaidStatusesFilter(paidStatusFilters)
        : undefined,
      paymentMethods: showPaymentAndCheckout && paymentMethodFilters.length > 0
        ? paymentMethodFilters
        : undefined,
      joinedAtStart: joinedAtStart || undefined,
      joinedAtEnd: joinedAtEnd || undefined,
      projectCodes: resolvedProjectCodes.length > 0 ? resolvedProjectCodes : undefined,
      commercialOwnerIds: commercialOwnerIdFilters.length > 0 ? commercialOwnerIdFilters : undefined,
      isInactive: false,
      declinedRescue: isDeclinedListing,
    }),
    [
      searchName,
      classificationFilters,
      leadTemperatureFilters,
      personaFilters,
      cancellationReasonFilters,
      investmentInterestFilters,
      startTimeFilters,
      mainGoalFilters,
      commercialStageFilters,
      paidStatusFilters,
      paymentMethodFilters,
      joinedAtStart,
      joinedAtEnd,
      resolvedProjectCodes,
      commercialOwnerIdFilters,
      isDeclinedListing,
      showPaymentAndCheckout,
    ],
  );

  const hasActiveFilters = !!(
    searchName
    || classificationFilters.length > 0
    || leadTemperatureFilters.length > 0
    || personaFilters.length > 0
    || cancellationReasonFilters.length > 0
    || investmentInterestFilters.length > 0
    || startTimeFilters.length > 0
    || mainGoalFilters.length > 0
    || (!isDeclinedListing && commercialStageFilters.length > 0)
    || (showPaymentAndCheckout && paidStatusFilters.length > 0)
    || (showPaymentAndCheckout && paymentMethodFilters.length > 0)
    || (joinedAtStart && joinedAtEnd)
    || (!lockedProjectCode && projectCodeFilters.length > 0)
    || commercialOwnerIdFilters.length > 0
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const {
    leads,
    pagination,
    loading,
    error,
    refresh,
    patchLeadOnboardingStatus,
    patchLead,
  } = useLeads(page, pageSize, filters);

  useCommercialLeadStatusPolling(
    leads,
    filters,
    pagination.totalRecords,
    patchLeadOnboardingStatus,
    () => refresh({ silent: true }),
  );

  useEffect(() => {
    let cancelled = false;

    setLoadingOwnerOptions(true);
    LeadService.listCommercialOwners()
      .then((result) => {
        if (cancelled) {
          return;
        }

        setCurrentPortalUserId(result.currentPortalUserId);
        setCommercialOwnerOptions(result.users.map((user) => ({
          value: String(user.id),
          label: formatCommercialOwnerLabel(
            user.label,
            user.id,
            result.currentPortalUserId,
          ),
        })));
      })
      .catch(() => {
        // LeadService already surfaces the error.
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingOwnerOptions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const getLeadStageSaveKey = useCallback((lead: CheckoutLead): string | null => {
    if (lead.qualification_id != null) {
      return `qualification:${lead.qualification_id}`;
    }
    if (lead.student_id != null) {
      return `student:${lead.student_id}`;
    }
    return null;
  }, []);

  const isStageSelectLocked = useCallback((lead: CheckoutLead): boolean => {
    const saveKey = getLeadStageSaveKey(lead);
    if (!saveKey) {
      return false;
    }
    return (stageLockUntilByKey[saveKey] ?? 0) > Date.now();
  }, [getLeadStageSaveKey, stageLockUntilByKey]);

  const lockStageSelect = useCallback((saveKey: string) => {
    const existingTimeout = stageLockTimeoutsRef.current[saveKey];
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    setStageLockUntilByKey((current) => ({
      ...current,
      [saveKey]: Date.now() + STAGE_CHANGE_LOCK_MS,
    }));

    stageLockTimeoutsRef.current[saveKey] = window.setTimeout(() => {
      delete stageLockTimeoutsRef.current[saveKey];
      setStageLockUntilByKey((current) => {
        if (!(saveKey in current)) {
          return current;
        }
        const next = { ...current };
        delete next[saveKey];
        return next;
      });
    }, STAGE_CHANGE_LOCK_MS);
  }, []);

  useEffect(() => () => {
    Object.values(stageLockTimeoutsRef.current).forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
  }, []);

  const handleCommercialStageSelect = useCallback((lead: CheckoutLead, nextStage: string) => {
    const saveKey = getLeadStageSaveKey(lead);
    if (!saveKey || isStageSelectLocked(lead) || savingStageKey === saveKey) {
      return;
    }

    const normalizedNext = nextStage ? normalizeCommercialStage(nextStage) : '';
    if (!normalizedNext) {
      return;
    }

    const currentStage = normalizeCommercialStage(lead.commercial_stage);
    if (normalizedNext === currentStage) {
      return;
    }

    if (currentStage && !canAdvanceCommercialStage(currentStage, normalizedNext)) {
      return;
    }

    setPendingStageChange({ lead, nextStage: normalizedNext });
  }, [getLeadStageSaveKey, isStageSelectLocked, savingStageKey]);

  const handleCommercialStageChange = useCallback(async () => {
    if (!pendingStageChange) {
      return;
    }

    const { lead, nextStage } = pendingStageChange;
    const saveKey = getLeadStageSaveKey(lead);
    if (!saveKey) {
      return;
    }

    const previousStage = lead.commercial_stage;
    patchLead(
      { studentId: lead.student_id, qualificationId: lead.qualification_id },
      { commercial_stage: nextStage },
    );
    setSavingStageKey(saveKey);

    try {
      const result = await LeadService.updateCommercialStage({
        studentId: lead.student_id,
        qualificationId: lead.qualification_id,
        commercialStage: nextStage,
      });
      patchLead(
        { studentId: lead.student_id, qualificationId: lead.qualification_id },
        { commercial_stage: result.commercialStage },
      );
      lockStageSelect(saveKey);
      setPendingStageChange(null);
    } catch {
      patchLead(
        { studentId: lead.student_id, qualificationId: lead.qualification_id },
        { commercial_stage: previousStage },
      );
    } finally {
      setSavingStageKey((current) => current === saveKey ? null : current);
    }
  }, [getLeadStageSaveKey, lockStageSelect, patchLead, pendingStageChange]);

  const handleRescueDeclinedLead = useCallback(async () => {
    const lead = pendingRescueLead;
    if (!lead) {
      return;
    }

    const saveKey = getLeadStageSaveKey(lead);
    if (!saveKey || !canRescueDeclinedLead(lead.commercial_stage)) {
      return;
    }

    setSavingRescueKey(saveKey);

    try {
      await LeadService.rescueDeclinedLead({
        studentId: lead.student_id,
        qualificationId: lead.qualification_id,
      });
      setPendingRescueLead(null);
      refresh();
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setSavingRescueKey((current) => current === saveKey ? null : current);
    }
  }, [getLeadStageSaveKey, pendingRescueLead, refresh]);

  const handleAssignOwnerToSelfConfirm = useCallback(async () => {
    const lead = pendingAssignToSelf;
    const qualificationId = lead?.qualification_id;
    if (lead == null || qualificationId == null) {
      return;
    }

    setAssigningOwnerQualificationId(qualificationId);
    try {
      const result = await LeadService.assignCommercialOwner({
        qualificationId,
        assignToSelf: true,
      });
      patchLead(
        { studentId: lead.student_id, qualificationId },
        {
          commercial_owner_portal_user_id: result.commercialOwnerPortalUserId,
          commercial_owner_name: result.commercialOwnerName,
        },
      );
      setPendingAssignToSelf(null);
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setAssigningOwnerQualificationId((current) => current === qualificationId ? null : current);
    }
  }, [patchLead, pendingAssignToSelf]);

  const handleUnassignFromSelfConfirm = useCallback(async () => {
    const lead = pendingUnassignFromSelf;
    const qualificationId = lead?.qualification_id;
    if (lead == null || qualificationId == null) {
      return;
    }

    setAssigningOwnerQualificationId(qualificationId);
    try {
      const result = await LeadService.assignCommercialOwner({
        qualificationId,
        ownerPortalUserId: null,
      });
      patchLead(
        { studentId: lead.student_id, qualificationId },
        {
          commercial_owner_portal_user_id: result.commercialOwnerPortalUserId,
          commercial_owner_name: result.commercialOwnerName,
        },
      );
      setPendingUnassignFromSelf(null);
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setAssigningOwnerQualificationId((current) => current === qualificationId ? null : current);
    }
  }, [patchLead, pendingUnassignFromSelf]);

  const handleOwnerEditConfirm = useCallback(async () => {
    if (!canPickOtherOwners || !pendingOwnerEdit) {
      return;
    }

    const selectedOwnerId = pendingOwnerEdit.ownerId.trim();
    const ownerPortalUserId = selectedOwnerId ? Number.parseInt(selectedOwnerId, 10) : null;
    if (selectedOwnerId && (!Number.isInteger(ownerPortalUserId) || (ownerPortalUserId ?? 0) <= 0)) {
      return;
    }

    setSubmittingOwnerEdit(true);
    try {
      const result = await LeadService.assignCommercialOwner({
        qualificationId: pendingOwnerEdit.qualificationId,
        ownerPortalUserId,
      });
      patchLead(
        { qualificationId: pendingOwnerEdit.qualificationId },
        {
          commercial_owner_portal_user_id: result.commercialOwnerPortalUserId,
          commercial_owner_name: result.commercialOwnerName,
        },
      );
      setPendingOwnerEdit(null);
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setSubmittingOwnerEdit(false);
    }
  }, [canPickOtherOwners, patchLead, pendingOwnerEdit]);

  const openPlanModal = useCallback((plan: CheckoutLeadPlan) => {
    setSelectedPlan(resolveLeadPlanProject(plan, planProjectById));
  }, [planProjectById]);

  const openStudentDetails = useCallback((studentId: number | null) => {
    if (studentId == null) return;
    navigate(`/alunas/${studentId}/editar`);
  }, [navigate]);

  const findLeadByPendingIds = useCallback((
    studentId: number | null,
    qualificationId: number | null,
  ) => leads.find((lead) => (
    (qualificationId != null && lead.qualification_id === qualificationId)
    || (studentId != null && lead.student_id === studentId)
  )), [leads]);

  const openCancellationModal = useCallback((lead: CheckoutLead) => {
    setPendingCancellationSave({
      studentId: lead.student_id,
      qualificationId: lead.qualification_id,
      studentName: lead.student_name,
      reason: lead.cancellation_reason ?? '',
    });
  }, []);

  const handleCancellationReasonConfirm = useCallback(async () => {
    if (!pendingCancellationSave) return;

    const selectedReason = pendingCancellationSave.reason.trim();
    if (!isLeadCancellationReason(selectedReason)) return;

    const currentLead = findLeadByPendingIds(
      pendingCancellationSave.studentId,
      pendingCancellationSave.qualificationId,
    );

    try {
      setSubmittingCancellationReason(true);
      const result = await LeadService.updateLeadCancellationReason({
        studentId: pendingCancellationSave.studentId,
        qualificationId: pendingCancellationSave.qualificationId,
        cancellationReason: selectedReason,
      });

      patchLead(
        {
          studentId: pendingCancellationSave.studentId,
          qualificationId: pendingCancellationSave.qualificationId,
        },
        {
          cancellation_reason: result.cancellationReason,
          qualification: currentLead?.qualification
            ? {
              ...currentLead.qualification,
              cancellation_reason: result.cancellationReason,
            }
            : {
              investment_interest: null,
              start_time_expectation: null,
              main_goal: null,
              training_relationship: null,
              training_experience: null,
              busy_routine_behavior: null,
              weekly_commitment_days: null,
              training_format: null,
              consulting_expectation: null,
              injury_limitation: null,
              cancellation_reason: result.cancellationReason,
            },
        },
      );
      setPendingCancellationSave(null);
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setSubmittingCancellationReason(false);
    }
  }, [findLeadByPendingIds, patchLead, pendingCancellationSave]);

  const openCheckoutForLead = useCallback(async (lead: CheckoutLead) => {
    if (lead.student_id != null) {
      setCheckoutModalLead(lead);
      return;
    }

    if (lead.qualification_id == null) {
      return;
    }

    try {
      setEnsuringCheckoutStudent(true);
      const result = await LeadService.ensureLeadStudent(lead.qualification_id);
      const nextLead = { ...lead, student_id: result.studentId };
      patchLead(
        { qualificationId: lead.qualification_id },
        { student_id: result.studentId },
      );
      setCheckoutModalLead(nextLead);
    } catch {
      // LeadService already surfaces the error.
    } finally {
      setEnsuringCheckoutStudent(false);
    }
  }, [patchLead]);

  if (error) return <EmptyState message={`Erro: ${error}`} />;

  return (
    <PageContainer>
      <CommercialMonthGoal />
      <CommercialFilters
        filters={queryFilters}
        listingMode={listingMode}
        loading={loading}
        totalRecords={pagination.totalRecords}
        hasActiveFilters={hasActiveFilters}
        setFilter={setFilter}
        setFilters={setFilters}
        onClearFilters={handleClearFilters}
        ownerOptions={commercialOwnerOptions}
        hideProjectFilter={Boolean(lockedProjectCode)}
      />

      <div className={styles['list-body']}>
        {loading && leads.length === 0 ? (
          <Loading />
        ) : leads.length === 0 ? (
          <EmptyState message={hasActiveFilters
            ? 'Nenhuma aluna encontrada com os filtros selecionados.'
            : isDeclinedListing
              ? 'Nenhuma aluna declinada.'
              : 'Nenhuma aluna encontrada.'} />
        ) : (
          <DataTable
            headers={[
              'Aluna',
              'Tags',
              'Clint',
              'Responsável',
              'Data',
              ...(showPaymentAndCheckout ? ['Pagamento'] : []),
              'Qualificação',
              'Desistência',
              ...(showPaymentAndCheckout
                ? [<span key="checkout" className={styles['header-center']}>Checkout</span>]
                : []),
            ]}
            gridTemplate={showPaymentAndCheckout
              ? COMMERCIAL_LEADS_GRID_TEMPLATE
              : COMMERCIAL_DECLINED_GRID_TEMPLATE}
            lastColumnIsActions={showPaymentAndCheckout}
            highlightTerm={searchName}
            className={[
              styles['commercial-table'],
              isDeclinedListing ? styles['commercial-table-declined'] : '',
              loading ? styles['commercial-table-loading'] : '',
            ].filter(Boolean).join(' ')}
          >
            {leads.map((lead) => {
              const leadTemperature = lead.leadTemperature;
              const leadPersona = lead.persona;
              const lifecycleClassification = shouldDisplayStudentClassificationBadge(lead.classification)
                ? lead.classification
                : null;
              const lifecycleColors = lifecycleClassification
                ? getStudentClassificationBadgeColors(lifecycleClassification)
                : null;

              const showProfileTags = shouldShowCommercialLeadProfileTags(lead);
              const visibleLifecycle = lifecycleClassification;
              const visibleTemperature = showProfileTags ? leadTemperature : null;
              const visiblePersona = showProfileTags ? leadPersona : null;

              return (
              <DataTable.Row
                key={String(lead.qualification_id ?? lead.checkout_id)}
                className={styles['lead-row']}
                sensitive={Boolean(lead.is_sensitive)}
              >
                <div className={styles['col-name']}>
                  <div className={styles['lead-name-row']}>
                    {lead.student_id != null ? (
                      <button
                        type="button"
                        className={styles['lead-name']}
                        onClick={() => openStudentDetails(lead.student_id)}
                      >
                        {lead.student_name}
                      </button>
                    ) : (
                      <span
                        className={styles['lead-name']}
                        style={{ cursor: 'default' }}
                      >
                        {lead.student_name}
                      </span>
                    )}
                    <CopyStudentNameButton studentName={lead.student_name} />
                    {lead.existingStudentMatch && (
                      <Tooltip content="Contato já cadastrado em outra aluna">
                        <button
                          type="button"
                          className={styles['existing-student-warning']}
                          onClick={() => setExistingStudentMatchModal(lead.existingStudentMatch)}
                          aria-label="Contato já cadastrado em outra aluna"
                        >
                          <span className={studentListStyles['warning-icon']} aria-hidden="true">
                            warning
                          </span>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  {lead.student_email ? (
                    <span className={styles['lead-email']}>{lead.student_email}</span>
                  ) : (
                    <span className={styles['lead-email-empty']}>—</span>
                  )}
                  {lead.student_phone && (
                    <span className={styles['lead-phone']}>
                      <Tooltip content="Abrir conversa no WhatsApp">
                        <button
                          type="button"
                          className={studentListStyles['btn-whatsapp']}
                          aria-label="Abrir conversa no WhatsApp"
                          onClick={() => openCommercialLeadWhatsApp(lead)}
                        >
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </button>
                      </Tooltip>
                      {formatCommercialLeadPhone(lead)}
                    </span>
                  )}
                </div>

                <div className={styles['col-tags']}>
                  {(lead.project || visibleLifecycle || visibleTemperature || visiblePersona || lead.is_reactivated || lead.is_sensitive || lead.is_pregnant || lead.is_red_flag) ? (
                    <>
                      <ProjectBadge project={lead.project} />
                      {visibleLifecycle && lifecycleColors && (
                        <ClassificationBadge
                          label={visibleLifecycle.label}
                          backgroundColor={lifecycleColors.background}
                          textColor={lifecycleColors.text}
                        />
                      )}
                      {visibleTemperature && (
                        <ClassificationBadge
                          label={visibleTemperature.label}
                          backgroundColor={visibleTemperature.colors.background}
                          textColor={visibleTemperature.colors.text}
                        />
                      )}
                      {visiblePersona && (
                        <ClassificationBadge
                          label={visiblePersona.label}
                          backgroundColor={visiblePersona.colors.background}
                          textColor={visiblePersona.colors.text}
                        />
                      )}
                      <ReactivatedBadge visible={lead.is_reactivated} />
                      <SensitiveStudentBadge visible={lead.is_sensitive} reason={lead.sensitive_reason} />
                      <PregnantStudentBadge visible={lead.is_pregnant} pregnantUntil={lead.pregnant_until} />
                      <RedFlagBadge visible={lead.is_red_flag} />
                    </>
                  ) : (
                    <span className={styles['tags-empty']}>—</span>
                  )}
                </div>

                <div className={styles['col-commercial-stage']}>
                  <div className={styles['commercial-stage-row']}>
                    {lead.qualification_id != null || lead.student_id != null ? (
                      <div className={styles['stage-select']}>
                        <SearchableSelect
                          value={normalizeCommercialStage(lead.commercial_stage)}
                          onChange={(value) => {
                            handleCommercialStageSelect(lead, value);
                          }}
                          options={getLeadStageSelectOptions(lead.commercial_stage)}
                          placeholder="Selecione"
                          allowEmpty={false}
                          disabled={
                            savingStageKey === getLeadStageSaveKey(lead)
                            || isStageSelectLocked(lead)
                          }
                          className={styles['stage-select-trigger']}
                        />
                      </div>
                    ) : lead.commercial_stage ? (
                      <span className={styles['commercial-stage-preview']}>
                        {getCommercialStageLabel(lead.commercial_stage)}
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                    {lead.commercial_stage === CommercialStage.DECLINED
                      && !lead.cancellation_reason && (
                      <Tooltip content="Aluna declinada sem motivo de desistência">
                        <button
                          type="button"
                          className={styles['cancellation-reason-warning']}
                          onClick={() => setDeclinedMissingReasonLead(lead)}
                          aria-label="Aluna declinada precisa informar o motivo da desistência"
                        >
                          <span className={studentListStyles['warning-icon']} aria-hidden="true">
                            warning
                          </span>
                        </button>
                      </Tooltip>
                    )}
                    {lead.clintSyncError && (
                      <Tooltip content="Erro na sincronização com a Clint">
                        <button
                          type="button"
                          className={styles['clint-sync-error-button']}
                          onClick={() => setClintSyncErrorModal({
                            studentName: lead.student_name,
                            error: lead.clintSyncError!,
                          })}
                          aria-label="Ver erro da sincronização com a Clint"
                        >
                          <span className={studentListStyles['warning-icon']} aria-hidden="true">
                            warning
                          </span>
                        </button>
                      </Tooltip>
                    )}
                    {isDeclinedListing && canRescueDeclinedLead(lead.commercial_stage) && (
                      <ViewLinkButton
                        label="Resgatar"
                        icon="undo"
                        disabled={savingRescueKey === getLeadStageSaveKey(lead)}
                        onClick={() => setPendingRescueLead(lead)}
                      />
                    )}
                  </div>
                </div>

                <div className={styles['col-owner']}>
                  <div className={styles['owner-row']}>
                    {lead.commercial_owner_name ? (
                      <span className={styles['owner-name']}>
                        {formatCommercialOwnerLabel(
                          lead.commercial_owner_name,
                          lead.commercial_owner_portal_user_id,
                          currentPortalUserId,
                        )}
                      </span>
                    ) : (
                      <span className={styles['owner-empty']}>Sem responsável</span>
                    )}
                    {lead.qualification_id != null && canPickOtherOwners && (
                      <Tooltip content="Alterar responsável">
                        <button
                          type="button"
                          className={styles['btn-edit-owner']}
                          aria-label="Alterar responsável"
                          onClick={() => setPendingOwnerEdit({
                            qualificationId: lead.qualification_id!,
                            studentName: lead.student_name,
                            ownerId: lead.commercial_owner_portal_user_id
                              ? String(lead.commercial_owner_portal_user_id)
                              : '',
                          })}
                        >
                          <Icon name="change_circle" size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  {lead.qualification_id != null
                    && currentPortalUserId != null
                    && currentPortalUserId !== lead.commercial_owner_portal_user_id && (
                    <ViewLinkButton
                      label="Atribuir a mim"
                      icon={null}
                      disabled={assigningOwnerQualificationId === lead.qualification_id}
                      onClick={() => setPendingAssignToSelf(lead)}
                    />
                  )}
                  {lead.qualification_id != null
                    && currentPortalUserId != null
                    && currentPortalUserId === lead.commercial_owner_portal_user_id
                    && !canPickOtherOwners && (
                    <ViewLinkButton
                      label="Desatribuir"
                      icon={null}
                      disabled={assigningOwnerQualificationId === lead.qualification_id}
                      onClick={() => setPendingUnassignFromSelf(lead)}
                    />
                  )}
                </div>

                <div className={styles['col-center']}>
                  <span>
                    {lead.joined_at ? formatDateTime(lead.joined_at) : '—'}
                  </span>
                </div>

                {showPaymentAndCheckout && (
                <div className={styles['col-payment']}>
                  <div className={styles['payment-method-row']}>
                    <StatusIndicator
                      active={lead.has_paid}
                      label={lead.has_paid ? 'Pago' : 'Não pago'}
                    />
                    <span>
                      {lead.has_paid
                        ? getPaymentMethodLabel(lead.selected_payment_method)
                        : 'Não pago'}
                    </span>
                  </div>
                  <div className={styles['payment-plan-row']}>
                    <span className={styles['payment-meta-label']}>Plano:</span>
                    <span className={styles['payment-plan-value']}>
                      <ViewLinkButton
                        label={lead.plan.name ?? lead.plan.description ?? '—'}
                        icon={null}
                        truncate
                        onClick={() => openPlanModal(lead.plan)}
                      />
                    </span>
                  </div>
                  {lead.has_paid && (
                    <span className={styles['payment-paid-at']}>
                      {`Pago em: ${lead.paid_at ? formatDateTime(lead.paid_at) : '—'}`}
                    </span>
                  )}
                </div>
                )}

                <div className={styles['col-qualification']}>
                  {lead.qualification ? (
                    <ViewLinkButton
                      label="Ver respostas"
                      icon={null}
                      onClick={() => setSelectedQualificationLead(lead)}
                    />
                  ) : (
                    <span>—</span>
                  )}
                </div>

                <div className={styles['col-cancellation']}>
                  {lead.cancellation_reason && (
                    <span className={styles['cancellation-preview']}>
                      {getLeadCancellationReasonLabel(lead.cancellation_reason)}
                    </span>
                  )}
                  <div className={styles['cancellation-actions']}>
                    <ViewLinkButton
                      label={lead.cancellation_reason ? 'Editar' : 'Registrar'}
                      icon={lead.cancellation_reason ? 'edit' : 'add_notes'}
                      onClick={() => openCancellationModal(lead)}
                    />
                    {lead.commercial_stage === CommercialStage.DECLINED
                      && !lead.cancellation_reason && (
                      <Tooltip content="Aluna declinada sem motivo de desistência">
                        <button
                          type="button"
                          className={styles['cancellation-reason-warning']}
                          onClick={() => setDeclinedMissingReasonLead(lead)}
                          aria-label="Aluna declinada precisa informar o motivo da desistência"
                        >
                          <span className={studentListStyles['warning-icon']} aria-hidden="true">
                            warning
                          </span>
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {showPaymentAndCheckout && (
                <div className={styles['col-onboarding']}>
                  {lead.has_paid && !lead.can_start_new_checkout ? (
                    <StatusIndicator
                      active
                      label="Checkout concluído"
                    />
                  ) : shouldOfferCommercialCheckout(lead) ? (
                    <ViewLinkButton
                      label="Fazer checkout"
                      icon="shopping_cart"
                      onClick={() => {
                        void openCheckoutForLead(lead);
                      }}
                      disabled={ensuringCheckoutStudent}
                    />
                  ) : (
                    <span>—</span>
                  )}
                </div>
                )}
              </DataTable.Row>
              );
            })}
          </DataTable>
        )}
      </div>

      {leads.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={selectedPlan !== null}
        title="Plano contratado"
        variant="info"
        icon="article"
        confirmLabel="Fechar"
        hideCancel
        onConfirm={() => setSelectedPlan(null)}
        onCancel={() => setSelectedPlan(null)}
      >
        {selectedPlan && <PlanModalContent plan={selectedPlan} />}
      </ConfirmModal>

      <ConfirmModal
        open={selectedQualificationLead !== null}
        title="Respostas da qualificação"
        variant="info"
        icon="fact_check"
        confirmLabel="Fechar"
        hideCancel
        onConfirm={() => setSelectedQualificationLead(null)}
        onCancel={() => setSelectedQualificationLead(null)}
      >
        {selectedQualificationLead && <QualificationModalContent lead={selectedQualificationLead} />}
      </ConfirmModal>

      <CommercialCheckoutLinkModal
        open={checkoutModalLead !== null}
        leadName={checkoutModalLead?.student_name ?? ''}
        studentId={checkoutModalLead?.student_id ?? null}
        qualificationId={checkoutModalLead?.qualification_id ?? null}
        initialPlanId={checkoutModalLead?.plan.id ?? null}
        onClose={() => setCheckoutModalLead(null)}
      />

      <ConfirmModal
        open={declinedMissingReasonLead !== null}
        title="Motivo da desistência pendente"
        message={
          declinedMissingReasonLead
            ? `${declinedMissingReasonLead.student_name} está com status Declinado e ainda não tem o motivo da desistência informado. Registre o motivo para manter o histórico comercial completo.`
            : ''
        }
        confirmLabel="Registrar motivo"
        cancelLabel="Fechar"
        variant="warning"
        icon="warning"
        onConfirm={() => {
          const lead = declinedMissingReasonLead;
          setDeclinedMissingReasonLead(null);
          if (lead) openCancellationModal(lead);
        }}
        onCancel={() => setDeclinedMissingReasonLead(null)}
      />

      <ConfirmModal
        open={Boolean(pendingCancellationSave)}
        title={pendingCancellationSave ? `Motivo da desistência — ${pendingCancellationSave.studentName}` : 'Motivo da desistência'}
        message={pendingCancellationSave ? 'Selecione o motivo informado pela aluna para desistir do processo.' : ''}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        icon="speaker_notes"
        loading={submittingCancellationReason}
        onCancel={() => setPendingCancellationSave(null)}
        onConfirm={handleCancellationReasonConfirm}
      >
        <div className={styles['cancellation-modal-fields']}>
          <FormSelect
            id="commercial-cancellation-reason"
            label="Motivo da desistência"
            value={pendingCancellationSave?.reason ?? ''}
            onChange={(event) => {
              const nextValue = event.target.value;
              setPendingCancellationSave((current) => (
                current ? { ...current, reason: nextValue } : current
              ));
            }}
            options={LEAD_CANCELLATION_REASON_OPTIONS}
            placeholder="Selecione o motivo"
            disabled={submittingCancellationReason}
            required
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingStageChange)}
        title="Alterar estágio"
        confirmLabel="Alterar"
        cancelLabel="Cancelar"
        variant="info"
        icon="swap_horiz"
        loading={Boolean(
          pendingStageChange && savingStageKey === getLeadStageSaveKey(pendingStageChange.lead),
        )}
        onCancel={() => {
          if (savingStageKey) {
            return;
          }
          setPendingStageChange(null);
        }}
        onConfirm={() => {
          void handleCommercialStageChange();
        }}
      >
        {pendingStageChange && (
          <p className={styles['first-contact-confirm-message']}>
            Deseja alterar o estágio de <strong>{pendingStageChange.lead.student_name}</strong>
            {' '}de{' '}
            <strong>
              {normalizeCommercialStage(pendingStageChange.lead.commercial_stage)
                ? getCommercialStageLabel(pendingStageChange.lead.commercial_stage)
                : 'sem estágio'}
            </strong>
            {' '}para{' '}
            <strong>{getCommercialStageLabel(pendingStageChange.nextStage)}</strong>?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingRescueLead)}
        title="Resgatar"
        confirmLabel="Resgatar"
        cancelLabel="Cancelar"
        variant="info"
        icon="undo"
        loading={Boolean(
          pendingRescueLead && savingRescueKey === getLeadStageSaveKey(pendingRescueLead),
        )}
        onCancel={() => {
          if (savingRescueKey) {
            return;
          }
          setPendingRescueLead(null);
        }}
        onConfirm={() => {
          void handleRescueDeclinedLead();
        }}
      >
        {pendingRescueLead && (
          <p className={styles['first-contact-confirm-message']}>
            Deseja voltar <strong>{pendingRescueLead.student_name}</strong> para 1º contato?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingAssignToSelf)}
        title="Atribuir a mim"
        confirmLabel="Atribuir"
        cancelLabel="Cancelar"
        variant="info"
        icon="person_add"
        loading={Boolean(
          pendingAssignToSelf
          && assigningOwnerQualificationId === pendingAssignToSelf.qualification_id,
        )}
        onCancel={() => {
          if (assigningOwnerQualificationId != null) {
            return;
          }
          setPendingAssignToSelf(null);
        }}
        onConfirm={() => {
          void handleAssignOwnerToSelfConfirm();
        }}
      >
        {pendingAssignToSelf && (
          <p className={styles['first-contact-confirm-message']}>
            Deseja atribuir <strong>{pendingAssignToSelf.student_name}</strong> a você?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingUnassignFromSelf)}
        title="Desatribuir"
        confirmLabel="Desatribuir"
        cancelLabel="Cancelar"
        variant="info"
        icon="person_remove"
        loading={Boolean(
          pendingUnassignFromSelf
          && assigningOwnerQualificationId === pendingUnassignFromSelf.qualification_id,
        )}
        onCancel={() => {
          if (assigningOwnerQualificationId != null) {
            return;
          }
          setPendingUnassignFromSelf(null);
        }}
        onConfirm={() => {
          void handleUnassignFromSelfConfirm();
        }}
      >
        {pendingUnassignFromSelf && (
          <p className={styles['first-contact-confirm-message']}>
            Deseja desatribuir <strong>{pendingUnassignFromSelf.student_name}</strong> de você?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingOwnerEdit) && canPickOtherOwners}
        title={pendingOwnerEdit ? `Trocar responsável — ${pendingOwnerEdit.studentName}` : 'Trocar responsável'}
        message="Deseja mesmo trocar o responsável desta aluna?"
        confirmLabel="Trocar"
        cancelLabel="Cancelar"
        variant="info"
        icon="change_circle"
        loading={submittingOwnerEdit}
        onCancel={() => setPendingOwnerEdit(null)}
        onConfirm={handleOwnerEditConfirm}
      >
        <div className={styles['owner-modal-fields']}>
          <SearchableSelect
            value={pendingOwnerEdit?.ownerId ?? ''}
            onChange={(value) => {
              setPendingOwnerEdit((current) => (
                current ? { ...current, ownerId: value } : current
              ));
            }}
            options={commercialOwnerOptions}
            placeholder={loadingOwnerOptions ? 'Carregando responsáveis...' : 'Selecione o responsável'}
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma responsável encontrada"
            disabled={submittingOwnerEdit || loadingOwnerOptions}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={existingStudentMatchModal !== null}
        title="Contato já cadastrado"
        variant="warning"
        icon="warning"
        confirmLabel="Abrir perfil"
        cancelLabel="Fechar"
        onConfirm={() => {
          const studentId = existingStudentMatchModal?.studentId ?? null;
          setExistingStudentMatchModal(null);
          openStudentDetails(studentId);
        }}
        onCancel={() => setExistingStudentMatchModal(null)}
      >
        {existingStudentMatchModal && (
          <div className={styles['existing-student-match-content']}>
            <p>
              Este lead usa um contato que já existe na aluna{' '}
              <strong>{existingStudentMatchModal.fullName || `#${existingStudentMatchModal.studentId}`}</strong>
              {existingStudentMatchModal.isInactive ? ' (inativa)' : ''}.
            </p>
            <ul>
              {existingStudentMatchModal.matchedByEmail && existingStudentMatchModal.email && (
                <li>
                  E-mail cadastrado: <strong>{existingStudentMatchModal.email}</strong>
                </li>
              )}
              {existingStudentMatchModal.matchedByPhone && formatExistingMatchPhone(existingStudentMatchModal) && (
                <li>
                  Celular cadastrado: <strong>{formatExistingMatchPhone(existingStudentMatchModal)}</strong>
                </li>
              )}
            </ul>
          </div>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={clintSyncErrorModal !== null}
        title="Erro na sincronização Clint"
        variant="danger"
        icon="error"
        confirmLabel="Fechar"
        hideCancel
        onConfirm={() => setClintSyncErrorModal(null)}
        onCancel={() => setClintSyncErrorModal(null)}
      >
        {clintSyncErrorModal && (
          <div className={styles['clint-sync-error-content']}>
            <p>
              Falha ao sincronizar o lead{' '}
              <strong>{clintSyncErrorModal.studentName}</strong> com a Clint.
            </p>
            <pre className={styles['clint-sync-error-message']}>
              {clintSyncErrorModal.error}
            </pre>
          </div>
        )}
      </ConfirmModal>
    </PageContainer>
  );
};

export default Commercial;
